import { z } from 'zod';
/**
 * Die Verträge der Kategorieauswahl — was die Kontierungsmaske vom Kontenkatalog
 * bekommt (Migration `0075`).
 *
 * **Hier steht nur die Form, nicht der Weg.** Endpunkt und Maske entstehen mit
 * `C-kontapi` und `V-kontmaske`; dieses Modul definiert vorab, wie ein
 * Katalogeintrag über die Leitung geht, damit beide Seiten dieselbe Form
 * benutzen und nicht zwei ähnliche erfinden.
 *
 * **Der Unterschied zu `accounts.ts` ist der Gegenstand und nicht die Technik.**
 * `accounts.ts` beschreibt eine **Kontenversion** — Nummer, Kontenart,
 * Steuerschlüssel, Gültigkeit — und damit eine buchhalterisch verbindliche
 * Angabe. Hier steht die **Anzeige**: Gruppe, Titel, Prosa, Stichworte. Die
 * beiden zusammenzulegen hiesse, einen Anzeigetext derselben Unveränderbarkeit
 * zu unterwerfen wie eine Kontenart (Migration `0018`) — ein Tippfehler in der
 * Beschreibung wäre dann eine neue Kontoversion.
 */
/**
 * Die Automatikfunktionen des DATEV-Kontenrahmens, wörtlich aus der Legende der
 * Quelle (SKR 03, Art-Nr. 19644, Seite 75):
 *
 * - `AV` — automatische Errechnung der **Vorsteuer**
 * - `AM` — automatische Errechnung der **Umsatzsteuer**
 *
 * Auf einem solchen Konto rechnet DATEV die Steuer selbst. Die Maske schlägt
 * dort **keinen** Steuerschlüssel vor, und der spätere Export setzt keinen: Ein
 * manuell mitgegebener Schlüssel ist auf einem Automatikkonto ein Fehlerbild und
 * lässt sich nur über den Berichtigungsschlüssel 4 aufheben.
 *
 * Bewusst **kein** `boolean`. „Automatik ja/nein" beantwortet nicht, welche
 * Steuer gerechnet wird — und genau das entscheidet, ob ein Eingangs- oder ein
 * Ausgangsvorgang betroffen ist.
 */
export const automatikFunktionen = ['AV', 'AM'];
/**
 * Ein Eintrag des Katalogs, so wie ihn die Maske anzeigt.
 *
 * `kontonummer` und nicht `accountId`: Der Katalog spricht über **das Konto**
 * und nicht über eine seiner Versionen. Ein Verweis auf eine Versionskennung
 * zeigte nach der ersten Fortschreibung auf die beendete Version — derselbe
 * Befund, den `docs/verfahrensdokumentation/kontenrahmen.md`, Abschnitt 8, für
 * `bank_account.gl_account_id` festhält.
 */
export const kontenkatalogEintragSchema = z
    .object({
    kontonummer: z.string().regex(/^[0-9]{4,6}$/, 'Eine Kontonummer hat vier bis sechs Ziffern'),
    /** Die Überschrift der linken Spalte, etwa „IT und Infrastruktur". */
    gruppe: z.string().min(1),
    /** Unser Anzeigetitel — nicht die amtliche Bezeichnung. */
    titel: z.string().min(1),
    /**
     * Die amtliche SKR03-Bezeichnung aus `account.name`, damit die Karte beides
     * zeigt: den Titel, unter dem ein Mensch sucht, und den Namen, unter dem die
     * Kanzlei das Konto kennt.
     */
    amtlicheBezeichnung: z.string().min(1),
    /** Die Prosa der Karte. */
    beschreibung: z.string().min(1),
    /** Suchstichworte. Eine Liste und keine Zeichenkette mit Trennzeichen. */
    stichworte: z.array(z.string().min(1)),
    /**
     * Die Automatikfunktion des Kontos, oder `null`. Siehe
     * {@link automatikFunktionen}.
     */
    automatik: z.enum(automatikFunktionen).nullable(),
    /**
     * Der Hinweis unter der Beschreibung. Für ein Konto mit dem Steuerschlüssel
     * `ABSTIMMEN` ist er in der Datenbank Pflicht (Trigger
     * `trg_kontenkatalog_eintrag`, Fall `E-387`) — hier bleibt er
     * `null`-fähig, weil die meisten Konten keinen tragen.
     */
    hinweis: z.string().nullable(),
    /**
     * Ob dieses Konto eine offene Abstimmung trägt — abgeleitet aus
     * `account.tax_key === 'ABSTIMMEN'` und **nicht** gespeichert.
     *
     * Die Maske braucht die Auskunft als Merkmal und nicht als Zeichenkette:
     * Ein Katalog, der `'ABSTIMMEN'` als Steuerschlüssel anzeigte, sähe aus wie
     * ein Konto mit einem sehr ungewöhnlichen Schlüssel.
     */
    abstimmungOffen: z.boolean(),
})
    .strict();
/**
 * Eine Gruppe mit ihren Einträgen — die Form, in der die linke Spalte der Maske
 * gefüllt wird.
 *
 * Die Gruppierung geschieht beim Ausliefern und nicht in der Maske: Die
 * Reihenfolge steht als **eine** Zahl je Eintrag in der Datenbank
 * (`kontenkatalog.rang`), und die Gruppen ordnen sich nach dem kleinsten Rang
 * ihrer Einträge. Wer die Gruppierung dem Frontend überliesse, hätte diese Regel
 * ein zweites Mal — und beim ersten Auseinanderlaufen zwei Reihenfolgen.
 */
export const kontenkatalogGruppeSchema = z
    .object({
    gruppe: z.string().min(1),
    eintraege: z.array(kontenkatalogEintragSchema).min(1),
})
    .strict();
/** Die Antwort der Kategorieauswahl: alle sichtbaren Gruppen in ihrer Reihenfolge. */
export const kontenkatalogAntwortSchema = z
    .object({ gruppen: z.array(kontenkatalogGruppeSchema) })
    .strict();
//# sourceMappingURL=kontenkatalog.js.map